// The question an add asks before it is sent, if any. SL1.0 parity: an add of 19 or
// more asks "The qty is greater than or equal to 19. Do you really wish to add N?" first
// (`doAddToCart()`, `cabinet_smanager.htm:399-400`). SL1.0's native `confirm()` becomes
// the design-system `Modal` (the MyAccount erasure-confirm pattern): the title is SL1.0's
// question verbatim, with Cancel and Add; Cancel adds nothing.
//
// Deviation from SL1.0 (PO decision, 2026-10-02): SL1.0's
// `doAddToCart()` also asks "This item is already in your cart. Do you wish to add N
// more?" when the product is already in the cart (`cabinet_smanager.htm:390-396`). SL2.0
// does not ask it: an add of a product already in the cart sums onto its line at once.
// Only the scan sheet picks a quantity before adding; every other add route adds one at
// a time, which never reaches 19, so they add without this hook.
import { useState } from 'react';
import { Button, Modal } from '../design-system';
import { largeQuantityConfirmationMessage, needsLargeQuantityConfirmation } from './lineQuantity';

export interface AddPrompt {
  /** The quantity the question on screen asks about, or null when none is asked. */
  readonly pending: number | null;
  /** Ask first when the add needs it, else add at once. */
  readonly request: (quantity: number) => void;
  /** The Shopper confirmed: the add is sent. */
  readonly accept: () => void;
  /** The Shopper cancelled: nothing is added. */
  readonly cancel: () => void;
}

export function useAddPrompt(onAdd: (quantity: number) => void): AddPrompt {
  const [pending, setPending] = useState<number | null>(null);

  const request = (quantity: number) => {
    if (needsLargeQuantityConfirmation(quantity)) setPending(quantity);
    else onAdd(quantity);
  };
  const accept = () => {
    const confirmed = pending;
    setPending(null);
    if (confirmed !== null) onAdd(confirmed);
  };
  const cancel = () => setPending(null);

  return { pending, request, accept, cancel };
}

export interface AddPromptModalProps {
  readonly prompt: AddPrompt;
  readonly productName: string;
}

/** The question as a design-system `Modal`, closed while nothing is pending. */
export function AddPromptModal({ prompt, productName }: AddPromptModalProps) {
  const { pending, accept, cancel } = prompt;
  return (
    <Modal
      open={pending !== null}
      onClose={cancel}
      title={pending === null ? '' : largeQuantityConfirmationMessage(pending)}
      footer={
        <>
          <Button variant="secondary" onClick={cancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            aria-label={`Add ${pending ?? ''} of ${productName} to basket`}
            onClick={accept}
          >
            Add
          </Button>
        </>
      }
    >
      {null}
    </Modal>
  );
}
